import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import DashboardLayout from "./DashboardLayout";
import { defaultSiteChromeSettings, normalizeSiteChromeSettings } from "../utils/siteChrome";
import { RUNTIME_API_BASE } from "../utils/runtimeBase";
import { sweetAlert } from "../utils/sweetAlerts";

const API = RUNTIME_API_BASE;
const hasMeaningfulText = (value) =>
  /[A-Za-z0-9\u0980-\u09FF]/.test(
    String(value || "")
      .replace(/<[^>]*>/g, " ")
      .replace(/&nbsp;/gi, " ")
      .replace(/\s+/g, " ")
      .trim()
  );

function RichTextEditor({ value, onChange }) {
  const editorRef = useRef(null);

  useEffect(() => {
    if (editorRef.current && editorRef.current.innerHTML !== (value || "")) {
      editorRef.current.innerHTML = value || "";
    }
  }, [value]);

  const runCommand = (command) => {
    document.execCommand(command, false, null);
    onChange(editorRef.current?.innerHTML || "");
  };

  return (
    <div style={{ border: "1px solid #dbe3f0", borderRadius: "14px", overflow: "hidden", background: "#fff" }}>
      <div style={{ display: "flex", gap: "8px", padding: "10px", borderBottom: "1px solid #e2e8f0", background: "#f8fafc" }}>
        <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => runCommand("bold")}>Bold</button>
        <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => runCommand("italic")}>Italic</button>
      </div>
      <div
        ref={editorRef}
        contentEditable
        suppressContentEditableWarning
        onInput={() => onChange(editorRef.current?.innerHTML || "")}
        style={{ minHeight: "96px", padding: "12px 14px", outline: "none", fontFamily: "'Anek Bangla', 'Segoe UI', sans-serif", lineHeight: 1.7 }}
      />
    </div>
  );
}
function TopbarFooterSetting() {
  const [form, setForm] = useState(defaultSiteChromeSettings);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let active = true;

    axios
      .get(`${API}/site-chrome-settings`, {
        params: { _ts: Date.now() },
        headers: { "Cache-Control": "no-cache" },
      })
      .then((response) => {
        if (!active) return;
        setForm(normalizeSiteChromeSettings(response.data));
      })
      .catch(() => {
        if (!active) return;
        setForm(defaultSiteChromeSettings);
      });

    return () => {
      active = false;
    };
  }, []);

  const setField = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const addTopbarNotice = () => {
    setForm((prev) => ({
      ...prev,
      topbar_notices: [...(Array.isArray(prev.topbar_notices) ? prev.topbar_notices : []), ""],
    }));
  };

  const updateTopbarNotice = (index, value) => {
    setForm((prev) => ({
      ...prev,
      topbar_notices: (Array.isArray(prev.topbar_notices) ? prev.topbar_notices : []).map((item, itemIndex) => (itemIndex === index ? value : item)),
    }));
  };

  const removeTopbarNotice = (index) => {
    setForm((prev) => ({
      ...prev,
      topbar_notices: (Array.isArray(prev.topbar_notices) ? prev.topbar_notices : []).filter((_, itemIndex) => itemIndex !== index),
    }));
  };

  const saveSettings = async () => {
    try {
      setSaving(true);
      const topbarNotices = (Array.isArray(form.topbar_notices) ? form.topbar_notices : [])
        .map((item) => String(item || ""))
        .filter((item) => hasMeaningfulText(item));
      await axios.post(`${API}/site-chrome-settings`, {
        ...form,
        topbar_notice: topbarNotices.join("\n"),
        topbar_notices: topbarNotices,
      });
      await sweetAlert("Topbar and footer settings saved successfully", {
        icon: "success",
        timer: 2200,
      });
    } catch (requestError) {
      const msg = requestError.response?.data?.message || "Topbar and footer settings save failed";
      await sweetAlert(msg, { icon: "error", timer: 3200 });
    } finally {
      setSaving(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="container-fluid" style={{ display: "grid", gap: "22px" }}>
        <div>
          <h3 style={{ marginBottom: "6px" }}>Topbar & Footer Setting</h3>
          <p className="text-muted mb-0">
            Public front-end notice bar and footer section er visible data ekhane theke edit korte parben.
          </p>
        </div>
        <div className="card border-0 shadow-sm">
          <div className="card-body d-flex justify-content-end">
            <button type="button" className="btn btn-primary px-4" onClick={saveSettings} disabled={saving}>
              {saving ? "Saving..." : "Save Topbar & Footer Setting"}
            </button>
          </div>
        </div>

        <div className="card border-0 shadow-sm">
          <div className="card-body">
            <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
              <div>
                <h5 className="mb-1">Topbar Scrolling Notice Manager</h5>
                <div className="text-muted">Homepage header er niche scrolling notice ekhane manage korun.</div>
              </div>
              <button type="button" className="btn btn-outline-primary" onClick={addTopbarNotice}>Add Notice</button>
            </div>
            <div className="d-grid gap-3">
              {(Array.isArray(form.topbar_notices) ? form.topbar_notices : []).map((notice, index) => (
                <div key={`topbar-notice-${index}`} className="border rounded-4 p-3 bg-light">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <strong>Notice {index + 1}</strong>
                    <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => removeTopbarNotice(index)}>Remove</button>
                  </div>
                  <RichTextEditor value={notice || ""} onChange={(value) => updateTopbarNotice(index, value)} />
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="card border-0 shadow-sm">
          <div className="card-body">
            <h5 className="mb-3">Footer Setting</h5>
            <div className="row g-3">
              <div className="col-lg-3">
                <label className="form-label fw-semibold">Brand Text</label>
                <input className="form-control" value={form.footer_eyebrow} onChange={(event) => setField("footer_eyebrow", event.target.value)} />
              </div>
              <div className="col-lg-9">
                <label className="form-label fw-semibold">Heading</label>
                <input className="form-control" value={form.footer_heading} onChange={(event) => setField("footer_heading", event.target.value)} />
              </div>
              <div className="col-12">
                <label className="form-label fw-semibold">Description</label>
                <textarea className="form-control" rows="4" value={form.footer_description} onChange={(event) => setField("footer_description", event.target.value)} />
              </div>
              <div className="col-lg-4">
                <label className="form-label fw-semibold">Address</label>
                <input className="form-control" value={form.footer_address} onChange={(event) => setField("footer_address", event.target.value)} />
              </div>
              <div className="col-lg-4">
                <label className="form-label fw-semibold">Phone</label>
                <input className="form-control" value={form.footer_phone} onChange={(event) => setField("footer_phone", event.target.value)} />
              </div>
              <div className="col-lg-4">
                <label className="form-label fw-semibold">Email</label>
                <input className="form-control" value={form.footer_email} onChange={(event) => setField("footer_email", event.target.value)} />
              </div>
              <div className="col-lg-4">
                <label className="form-label fw-semibold">Facebook URL</label>
                <input className="form-control" value={form.facebook_url} onChange={(event) => setField("facebook_url", event.target.value)} />
              </div>
              <div className="col-lg-4">
                <label className="form-label fw-semibold">WhatsApp URL</label>
                <input className="form-control" value={form.whatsapp_url} onChange={(event) => setField("whatsapp_url", event.target.value)} />
              </div>
              <div className="col-lg-4">
                <label className="form-label fw-semibold">Instagram URL</label>
                <input className="form-control" value={form.instagram_url} onChange={(event) => setField("instagram_url", event.target.value)} />
              </div>
              <div className="col-lg-6">
                <label className="form-label fw-semibold">LinkedIn URL</label>
                <input className="form-control" value={form.linkedin_url} onChange={(event) => setField("linkedin_url", event.target.value)} />
              </div>
              <div className="col-lg-6">
                <label className="form-label fw-semibold">Twitter URL</label>
                <input className="form-control" value={form.twitter_url} onChange={(event) => setField("twitter_url", event.target.value)} />
              </div>
              <div className="col-12">
                <label className="form-label fw-semibold">Copyright Text</label>
                <input className="form-control" value={form.footer_copyright} onChange={(event) => setField("footer_copyright", event.target.value)} />
              </div>
            </div>
          </div>
        </div>

        <div className="d-flex justify-content-end">
          <button type="button" className="btn btn-primary px-4" onClick={saveSettings} disabled={saving}>
            {saving ? "Saving..." : "Save Topbar & Footer Setting"}
          </button>
        </div>
      </div>
    </DashboardLayout>
  );
}

export default TopbarFooterSetting;



